import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Users, ArrowRight, Lightbulb, Flame, MessageSquare } from 'lucide-react';

const DailyQuestion: React.FC = () => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      const diff = endOfDay.getTime() - now.getTime();
      setTimeLeft({
        hours: Math.floor(diff / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleAnswer = () => {
    navigate('/forum?category=not_istek');
  };

  const pad = (n: number) => n.toString().padStart(2, '0');

  return (
    <section style={{ padding: isMobile ? '60px 16px' : '80px 24px 100px', position: 'relative' }}>
      <div style={{
        position: 'absolute', top: '50%', right: '0', width: '400px', height: '400px',
        background: 'radial-gradient(circle, var(--accent-primary-soft) 0%, transparent 70%)',
        borderRadius: '50%', transform: 'translateY(-50%)', pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
        {/* Section badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center' as const, marginBottom: isMobile ? '24px' : '40px' }}
        >
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 18px',
            borderRadius: '50px', fontSize: '13px', fontWeight: '600', color: '#f59e0b',
            background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)',
          }}>
            <Lightbulb size={14} />
            <span>Her Gün Yeni Soru</span>
          </div>
        </motion.div>

        {/* Main Card */}
        <motion.div
          className="animated-border"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          style={{ position: 'relative' }}
        >
          <div style={{
            background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(30px)',
            borderRadius: '24px', padding: isMobile ? '24px 20px' : '48px',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px',
              background: 'radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 70%)',
              borderRadius: '50%', pointerEvents: 'none',
            }} />

            {/* Header Row */}
            <div style={{
              display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '20px' : '32px',
              flexDirection: isMobile ? 'column' as const : 'row' as const,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  style={{
                    width: isMobile ? '44px' : '56px', height: isMobile ? '44px' : '56px',
                    borderRadius: isMobile ? '12px' : '16px',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 8px 30px rgba(245, 158, 11, 0.25)', flexShrink: 0,
                  }}
                >
                  <Calendar size={isMobile ? 20 : 26} color="white" />
                </motion.div>

                <h2 style={{
                  fontSize: isMobile ? '1.3rem' : '1.8rem', fontWeight: '800', margin: 0,
                  fontFamily: "'Space Grotesk', sans-serif",
                }}>
                  <span className="gradient-text-white">Bugünün Sorusu</span>
                </h2>
              </div>

              {/* Timer */}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                <Flame size={14} color="#ef4444" />
                <div style={{ display: 'flex', gap: '3px', fontFamily: "'Space Grotesk', monospace" }}>
                  {[pad(timeLeft.hours), pad(timeLeft.minutes), pad(timeLeft.seconds)].map((val, i) => (
                    <React.Fragment key={i}>
                      <span style={{
                        background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)',
                        borderRadius: '6px', padding: isMobile ? '4px 8px' : '6px 10px',
                        fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: '#ef4444',
                        minWidth: isMobile ? '32px' : '38px', textAlign: 'center' as const,
                      }}>
                        {val}
                      </span>
                      {i < 2 && <span style={{ color: '#ef4444', fontWeight: '700', fontSize: isMobile ? '12px' : '14px' }}>:</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            {/* Question */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              style={{
                fontSize: isMobile ? '1rem' : '1.3rem', color: '#e2e8f0',
                marginBottom: isMobile ? '24px' : '36px', lineHeight: '1.7', fontWeight: '400',
                borderLeft: '3px solid rgba(245, 158, 11, 0.4)', paddingLeft: isMobile ? '14px' : '20px',
              }}
            >
              Bir fonksiyonun türevi nedir ve nasıl hesaplanır? Türev kavramının geometrik anlamını açıklayarak, temel türev alma kurallarını örneklerle gösteriniz.
            </motion.p>

            {/* CTA Button */}
            <motion.button
              type="button"
              onClick={handleAnswer}
              className="btn-shimmer"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white',
                padding: isMobile ? '14px 28px' : '16px 36px',
                borderRadius: '14px', border: 'none', fontWeight: '600', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '10px',
                fontSize: isMobile ? '15px' : '16px',
                boxShadow: '0 8px 30px rgba(245, 158, 11, 0.25)',
                width: isMobile ? '100%' : 'auto', justifyContent: 'center',
              }}
            >
              <MessageSquare size={18} />
              <span>Foruma git ve tartış</span>
              <ArrowRight size={16} />
            </motion.button>

            {/* Stats Footer */}
            <div style={{
              display: 'flex', gap: isMobile ? '16px' : '28px', marginTop: isMobile ? '20px' : '32px',
              paddingTop: isMobile ? '16px' : '24px', borderTop: '1px solid rgba(51, 65, 85, 0.15)',
              flexWrap: 'wrap' as const,
            }}>
              {[
                { icon: Clock, text: '24 saat aktif', color: 'var(--accent-primary)' },
                { icon: Users, text: 'Toplulukla paylaş', color: '#06b6d4' },
                { icon: Calendar, text: 'Her gün yeni soru', color: '#8b5cf6' },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  viewport={{ once: true }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: isMobile ? '12px' : '13px', fontWeight: '500' }}
                >
                  <stat.icon size={isMobile ? 13 : 15} color={stat.color} style={{ opacity: 0.7 }} />
                  <span style={{ color: '#64748b' }}>{stat.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DailyQuestion;
